import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartType,
  Legend,
  LinearScale,
  Title,
  Tooltip,
  TooltipModel,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { ScanDataEnum } from '../scanDataApi/scanDataDefinitions';
import { selectHoverRowsideId, setHoverRowsideId } from '../select/selectSlice';
import { RowsideChartTooltip, TooltipData } from './RowsideChartTooltip';
import { useBlueberryEstimatedCountRowsideStackBarChart } from './hooks/useBlueberryEstimatedCountRowsideStackBarChart';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin
);

ChartJS.defaults.font.size = 16;
ChartJS.defaults.borderColor = '#7c7e80'; // darkTheme.grey.muted (set outside of theme context here)

interface Props {
  plugins?: object;
}

const BarChart: React.FC<Props> = ({ plugins }) => {
  const dispatch = useAppDispatch();
  const chartRef = React.useRef<ChartJS<'bar', number[], unknown> | null>(null);
  const currHoverRowsideId = useAppSelector(selectHoverRowsideId);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const initialTooltipData = {
    dataPoints: [],
    left: 0,
    bottom: 0,
  };

  const [tooltipData, setTooltipData] =
    useState<TooltipData>(initialTooltipData);

  const closeTooltip = () => {
    setIsTooltipOpen(false);
    if (currHoverRowsideId) {
      dispatch(setHoverRowsideId(''));
    }
  };

  const openTooltip = () => {
    setIsTooltipOpen(true);
    const dataIndex = tooltipData.dataPoints?.at(0)?.dataIndex;

    if (data?.rawData && dataIndex !== undefined) {
      const newHoverRowsideId =
        data.rawData[dataIndex][ScanDataEnum.rowside_id];
      if (currHoverRowsideId !== newHoverRowsideId) {
        dispatch(setHoverRowsideId(newHoverRowsideId));
      }
    }
  };

  const customTooltipPlugin = {
    tooltip: {
      events: ['mousemove', 'click'] as Array<keyof HTMLElementEventMap>,
      enabled: false,
      external: ({ tooltip }: { tooltip: TooltipModel<ChartType> }) => {
        if (tooltip.opacity === 0 && isTooltipOpen) {
          closeTooltip();
          return;
        }

        const newData = {
          dataPoints: tooltip.dataPoints,
          left: tooltip.caretX,
          bottom: tooltip.caretY,
        };

        if (tooltipData.left !== newData.left) {
          setTooltipData(newData);
        }
      },
    },
  };

  useEffect(() => {
    if (tooltipData.dataPoints?.length > 0) {
      openTooltip();
    }
  }, [tooltipData]);

  const { data, options } = useBlueberryEstimatedCountRowsideStackBarChart(
    chartRef.current,
    { ...plugins, ...customTooltipPlugin }
  );

  return (
    <>
      <RowsideChartTooltip
        data={tooltipData}
        chartArea={chartRef.current?.chartArea}
        open={isTooltipOpen}
        onOpen={openTooltip}
        onClose={closeTooltip}
      />
      {data && (
        <Bar
          ref={chartRef}
          data={data}
          options={{
            ...options,
            onClick: openTooltip, // allows user to open the tooltip if it doesn't immediately come up on hover
            interaction: { mode: 'index' },
          }}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            minWidth: '300px',
            position: 'absolute', // this is needed to make the chart responsive - https://github.com/chartjs/Chart.js/issues/9001
          }}
        />
      )}
    </>
  );
};

export default BarChart;
